import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type {
  Medication,
  PersonalProfile,
  Provider,
  Report,
} from './types'

const PAGE_MARGIN = 44
const PAGE_WIDTH = 595.28
const PAGE_CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2
const PRIMARY_RGB: [number, number, number] = [15, 118, 110]
const TEXT_RGB: [number, number, number] = [39, 39, 42]
const MUTED_RGB: [number, number, number] = [82, 82, 91]

type JsPdfWithTables = jsPDF & {
  lastAutoTable?: {
    finalY?: number
  }
}

export interface ReportPdfMemberContext {
  accountEmail: string
  medications: Medication[]
  memberName: string
  personalProfile: PersonalProfile
  providers: Provider[]
}

function normalizeFileSegment(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
}

function formatTodayForFileName(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')
  return `${year}${month}${day}`
}

function writeSectionTitle(doc: jsPDF, title: string, yPosition: number): number {
  doc.setTextColor(...TEXT_RGB)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(title, PAGE_MARGIN, yPosition)
  return yPosition + 10
}

function writeParagraph(doc: jsPDF, text: string, yPosition: number): number {
  const normalizedText = text.trim() || 'No summary available.'
  const lines = doc.splitTextToSize(normalizedText, PAGE_CONTENT_WIDTH)

  doc.setTextColor(...MUTED_RGB)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(lines, PAGE_MARGIN, yPosition)

  return yPosition + lines.length * 12 + 6
}

function nextTableY(doc: JsPdfWithTables, fallback: number): number {
  return typeof doc.lastAutoTable?.finalY === 'number' ? doc.lastAutoTable.finalY + 14 : fallback
}

function toDisplayValue(value: string): string {
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : 'Not provided'
}

function formatAddress(profile: PersonalProfile): string {
  const segments = [
    profile.addressLine1.trim(),
    profile.addressLine2.trim(),
    profile.city.trim(),
    profile.state.trim(),
    profile.postalCode.trim(),
    profile.country.trim(),
  ].filter((segment) => segment.length > 0)

  return segments.length > 0 ? segments.join(', ') : 'Not provided'
}

function resolveMemberDisplayName(memberContext: ReportPdfMemberContext): string {
  const preferredName = memberContext.personalProfile.preferredName.trim()
  if (preferredName.length > 0) {
    return preferredName
  }

  const fullName = memberContext.personalProfile.fullName.trim()
  if (fullName.length > 0) {
    return fullName
  }

  return toDisplayValue(memberContext.memberName)
}

export function buildReportPdfFileName(report: Report): string {
  const safeTitle = normalizeFileSegment(report.title || 'health-report')
  return `${safeTitle || 'health-report'}-${formatTodayForFileName()}.pdf`
}

export async function createReportPdfBlob(
  report: Report,
  memberContext: ReportPdfMemberContext
): Promise<Blob> {
  const doc = new jsPDF({ compress: true, format: 'a4', unit: 'pt' }) as JsPdfWithTables
  const memberDisplayName = resolveMemberDisplayName(memberContext)
  const emergencyContact = [
    memberContext.personalProfile.emergencyContactName.trim(),
    memberContext.personalProfile.emergencyContactPhone.trim(),
  ]
    .filter((value) => value.length > 0)
    .join(' - ')

  let cursorY = 58
  doc.setTextColor(...PRIMARY_RGB)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text(report.title, PAGE_MARGIN, cursorY)

  cursorY += 18
  doc.setTextColor(...MUTED_RGB)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(
    `Focus: ${report.focus}  |  Generated: ${report.createdAt}  |  Format: PDF`,
    PAGE_MARGIN,
    cursorY
  )

  cursorY += 14
  doc.setDrawColor(...PRIMARY_RGB)
  doc.setLineWidth(1)
  doc.line(PAGE_MARGIN, cursorY, PAGE_WIDTH - PAGE_MARGIN, cursorY)

  cursorY += 18
  cursorY = writeSectionTitle(doc, 'Patient Information', cursorY)
  autoTable(doc, {
    startY: cursorY,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    theme: 'grid',
    head: [['Field', 'Value']],
    body: [
      ['Name', memberDisplayName],
      ['Account email', toDisplayValue(memberContext.accountEmail)],
      ['Date of birth', toDisplayValue(memberContext.personalProfile.dateOfBirth)],
      ['Phone', toDisplayValue(memberContext.personalProfile.phone)],
      ['Address', formatAddress(memberContext.personalProfile)],
      ['Insurance provider', toDisplayValue(memberContext.personalProfile.insuranceProvider)],
      ['Emergency contact', emergencyContact || 'Not provided'],
    ],
    styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
    headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 170 },
      1: { cellWidth: 'auto' },
    },
  })
  cursorY = nextTableY(doc, cursorY + 24)

  cursorY = writeSectionTitle(doc, 'Care Team And Medications', cursorY)
  autoTable(doc, {
    startY: cursorY,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    theme: 'grid',
    head: [['Category', 'Details']],
    body: [
      [
        'Providers',
        memberContext.providers.length > 0
          ? memberContext.providers
              .map((provider) => `${provider.name} (${provider.type}) - ${provider.connected ? 'Connected' : 'Disconnected'}`)
              .join('; ')
          : 'No providers connected.',
      ],
      [
        'Medications',
        memberContext.medications.length > 0
          ? memberContext.medications
              .map(
                (medication) =>
                  `${medication.name} ${medication.dosage} (${medication.frequency}, ${medication.status})`
              )
              .join('; ')
          : 'No medications listed.',
      ],
    ],
    styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
    headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 'auto' },
    },
  })
  cursorY = nextTableY(doc, cursorY + 24)

  cursorY = writeSectionTitle(doc, 'Executive Summary', cursorY)
  cursorY = writeParagraph(doc, report.summary, cursorY)

  const generationConfig = report.generationConfig
  const generationContext = report.generationContext
  autoTable(doc, {
    startY: cursorY,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    theme: 'grid',
    head: [['Metadata', 'Value']],
    body: [
      ['AI analysis', report.includesAiAnalysis !== false ? 'Included' : 'Not included'],
      ['Confidence score', report.includesAiAnalysis !== false ? `${report.confidence}%` : 'N/A'],
      ['Record scope', generationConfig ? generationConfig.recordScope : 'Not specified'],
      ['Included records', generationContext ? `${generationContext.includedRecordCount}` : '0'],
      ['Included PDF records', generationContext ? `${generationContext.includedPdfCount}` : '0'],
      ['Prior reports context', generationContext ? `${generationContext.includedPriorReportCount}` : '0'],
    ],
    styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
    headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 170 },
      1: { cellWidth: 'auto' },
    },
  })
  cursorY = nextTableY(doc, cursorY + 24)

  if (report.narrative) {
    cursorY = writeSectionTitle(doc, 'Key Findings', cursorY)
    autoTable(doc, {
      startY: cursorY,
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
      theme: 'grid',
      head: [['Tone', 'Finding']],
      body: report.narrative.findings.map((finding) => [finding.tone.toUpperCase(), finding.detail]),
      styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
      headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 'auto' },
      },
    })
    cursorY = nextTableY(doc, cursorY + 24)

    cursorY = writeSectionTitle(doc, 'Recommended Actions', cursorY)
    autoTable(doc, {
      startY: cursorY,
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
      theme: 'grid',
      head: [['Priority', 'Action']],
      body: report.narrative.actions.map((action) => [action.priority.toUpperCase(), action.detail]),
      styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
      headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 'auto' },
      },
    })
    cursorY = nextTableY(doc, cursorY + 24)

    cursorY = writeSectionTitle(doc, 'Source Coverage', cursorY)
    autoTable(doc, {
      startY: cursorY,
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
      theme: 'grid',
      head: [['Source']],
      body: report.narrative.sourceCoverage.map((source) => [source]),
      styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
      headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
    })
    cursorY = nextTableY(doc, cursorY + 24)

    if (report.narrative.priorContext.length > 0) {
      cursorY = writeSectionTitle(doc, 'Prior Report Context', cursorY)
      autoTable(doc, {
        startY: cursorY,
        margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
        theme: 'grid',
        head: [['Context']],
        body: report.narrative.priorContext.map((contextLine) => [contextLine]),
        styles: { cellPadding: 6, fontSize: 9, textColor: TEXT_RGB },
        headStyles: { fillColor: PRIMARY_RGB, fontStyle: 'bold', textColor: [255, 255, 255] },
      })
    }
  }

  return doc.output('blob')
}
